import { clear, element, forEachElement } from "../util/Utils";
import { SortingForm } from "../sorting/SortingForm";
import { FacetValueTags } from "../filter/FacetValueTags";
import type { Appendable } from "../util/Appendable";
import { UpdateReferencedAction } from "../filter/UpdateReferencedAction";
import { SubmitOnChangeListener } from "../filter/SubmitOnChange";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { setChangeLabels } from "../filter/FilterTracking";
import type { DresonRuleResolvedEvent, FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import FilterSwitchBanner from "../filterBanner/FilterSwitchBanner";
import { updateLabel } from "../tracking/FeatureTracking";
import { events } from "../events";
import { eventQBus } from "../types/EventQBus";
import { NextBestActionComponent } from "../nextBestAction/NextBestActionComponent";
import { SvelteFacet } from "../filter/SvelteFacet";
import { SvelteFacetValue } from "../filter/SvelteFacetValue";
import { FacetValueTagsComponent } from "../facetvaluetags/FacetValueTagsComponent";
import { isDesktop } from "../util/Breakpoint";

const CLASS_REFINEMENT_BAR = "find_refinementBar";
const SELECTOR_REFINEMENT_BAR = `.${CLASS_REFINEMENT_BAR}`;

const ID_SORTING_FORM_ANCHOR = "heureka_desktopSorting";
const SELECTOR_SORTING_FORM_ANCHOR = `#${ID_SORTING_FORM_ANCHOR}`;
const ID_FACET_VALUE_TAGS_ANCHOR = "find_facetValueTags";
const SELECTOR_FACET_VALUE_TAGS_ANCHOR = `#${ID_FACET_VALUE_TAGS_ANCHOR}`;
const ID_BANNER_ANCHOR = "heureka_filterBanner";
const SELECTOR_BANNER_ANCHOR = `#${ID_BANNER_ANCHOR}`;
const SELECTOR_FACET_VALUE_TAGS = `${SELECTOR_REFINEMENT_BAR} ${SELECTOR_FACET_VALUE_TAGS_ANCHOR}`;

const UPDATE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: undefined,
  san_FilterMethod: "x_single_top",
};

export class RefinementBar {
  /*               */
  protected constructor(
    private readonly sortingFormAnchor: HTMLElement | null,
    private readonly facetValueTagsAnchor: HTMLElement | null,
    private readonly filterBannerAnchor: HTMLElement | null,
  ) {}

  static #create(elem: HTMLElement) {
    /*                                               */
    if (!document.getElementById(ID_BANNER_ANCHOR) && document.querySelector(SELECTOR_REFINEMENT_BAR)) {
      const ottoSwitchBannerAnchor = document.createElement("div");
      ottoSwitchBannerAnchor.id = ID_BANNER_ANCHOR;
      document.querySelector(SELECTOR_REFINEMENT_BAR)?.append(ottoSwitchBannerAnchor);
    }
    return new RefinementBar(
      element(SELECTOR_SORTING_FORM_ANCHOR, elem),
      element(SELECTOR_FACET_VALUE_TAGS_ANCHOR, elem),
      element(SELECTOR_BANNER_ANCHOR, elem),
    );
  }

  static facetValueTagsElement(rootElement?: ParentNode | null): HTMLElement | null {
    return element(SELECTOR_FACET_VALUE_TAGS, rootElement);
  }

  /*                  */

  static forEach(callback: (input: RefinementBar, index: number) => void, rootElement?: ParentNode) {
    forEachElement<HTMLElement>(
      SELECTOR_REFINEMENT_BAR,
      (elem, index) => callback(RefinementBar.#create(elem), index),
      rootElement,
    );
  }

  /*               */

  static lockAll() {
    RefinementBar.forEach((refinementBar) => refinementBar.lock());
  }

  static unlockAll() {
    RefinementBar.forEach((refinementBar) => refinementBar.unlock());
  }

  static register() {
    events.onFilterSectionLoaded(RefinementBar.initAll);
    eventQBus.on("ftfind.tilelist.loaded", RefinementBar.initAll);

    /*      */
    eventQBus.on("ftfind.dresonRule.resolve", RefinementBar.lockAll);

    /*        */
    eventQBus.on("ftfind.dresonRule.resolved", RefinementBar.onDresonRuleResolved);
    events.onRefinementBarLoaded(RefinementBar.unlockAll);
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    RefinementBar.forEach((refinementBar) => refinementBar.init(), rootElement);
    events.refinementBarLoaded();
  }

  static onDresonRuleResolved(data: DresonRuleResolvedEvent) {
    /*                       */
    if (data.count == 0) {
      RefinementBar.unlockAll();
    }
  }

  init() {
    const form: SortingForm | undefined = SortingForm.template();
    if (form) {
      this.sortingForm = form.clone();
    }

    this.clearRefinementForm();
    this.clearFilterBanner();

    const facetValueTags = FacetValueTags.template()?.clone();
    if (facetValueTags) {
      /*                           */
      /*                                                                                                      */
      /*                                               */
      const insertionIndex = facetValueTags.findIndexOf(
        (facetValueTag) => facetValueTag.isCategoryPathFacetValueTagForm,
      );
      /*                                                                                             */
      if (insertionIndex >= 0) {
        /*                                                                               */
        const categoryPathFacet = SvelteFacet.facetName("kategorien");
        if (categoryPathFacet) {
          /*                                  */
          facetValueTags.removeFacetValueTagsOf((facetValueTag) => facetValueTag.facetName === "kategorien");

          const categoryPathFacetValueTags = this.createFacetValueTagsFrom(categoryPathFacet.selectedValues);
          facetValueTags.appendFacetValues(categoryPathFacetValueTags, insertionIndex);
          facetValueTags.removeFacetValueTagsOf((facetValueTag) => facetValueTag.isCategoryPathFacetValueTagForm);
        }
      }
    }

    let nextBestActionComponent: NextBestActionComponent;
    if (NextBestActionComponent.isVisible()) {
      nextBestActionComponent = NextBestActionComponent.template()?.clone();
      this.addToRefinementForm(nextBestActionComponent);
    } else {
      const facetValueTagsComponent = FacetValueTagsComponent.template()?.clone();
      this.addToRefinementForm(facetValueTagsComponent);
    }
    this.addToFilterBanner(FilterSwitchBanner.fromTemplate()?.clone());

    /*                                                          */
    if (isDesktop() || (facetValueTags && facetValueTags.facetValueTags.length > 0) || !!nextBestActionComponent) {
      this.hideRefinementBarPlaceholder();
    }
  }

  private hideRefinementBarPlaceholder() {
    const refinementBarPlaceholder = document.getElementById("heureka_refinementBar__placeholder");
    if (
      refinementBarPlaceholder &&
      !refinementBarPlaceholder.classList.contains("heureka_refinementBar__placeholder--hidden")
    ) {
      refinementBarPlaceholder.classList.add("heureka_refinementBar__placeholder--hidden");
    }
  }

  private createFacetValueTagsFrom(facetValues: SvelteFacetValue[]): SvelteFacetValue[] {
    return (
      facetValues?.map((facetValue) => {
        const facetValueTag = facetValue.clone().clearStatus().transformToFacetValueTag().withRef(facetValue.id);
        if (facetValueTag.facetValue) {
          UpdateReferencedAction.on(facetValueTag.facetValue);
          if (facetValue.facetValue?.form?.id) {
            SubmitOnChangeListener.onForForm(facetValueTag.facetValue, facetValue.facetValue.form.id, () => {
              this.setFacetValueTagTrackingLabelsOn(facetValue.facetValue);
            });
          }
        }
        return facetValueTag;
      }) || []
    );
  }

  private setFacetValueTagTrackingLabelsOn(facetValueInput: HTMLInputElement | null | undefined) {
    updateLabel(facetValueInput, "san_FacetLocation", undefined);
    if (facetValueInput?.form) {
      setChangeLabels(facetValueInput?.form, UPDATE_TRACKING_LABELS);
    }
  }

  /*                       */

  public lock() {
    this.sortingFormAnchor?.classList.add("find_refinementBar--loading");
    this.facetValueTagsAnchor?.classList.add("find_refinementBar--loading");
    this.filterBannerAnchor?.classList.add("find_refinementBar--loading");
    return this;
  }

  public unlock() {
    this.sortingFormAnchor?.classList.remove("find_refinementBar--loading");
    this.facetValueTagsAnchor?.classList.remove("find_refinementBar--loading");
    this.filterBannerAnchor?.classList.remove("find_refinementBar--loading");
    return this;
  }

  set sortingForm(form: SortingForm) {
    const anchor = this.sortingFormAnchor;
    if (anchor) {
      clear(anchor);
      anchor.appendChild(form.form);
    }
  }

  private clearRefinementForm() {
    const anchor = this.facetValueTagsAnchor;
    if (anchor) {
      clear(anchor);
    }
  }

  private addToRefinementForm(form: Appendable | undefined) {
    const anchor = this.facetValueTagsAnchor;
    if (anchor && form) {
      form.appendTo(anchor);
    }
  }

  private addToFilterBanner(elem: Appendable | undefined) {
    const anchor = this.filterBannerAnchor;
    if (anchor && elem) {
      elem.appendTo(anchor);
    }
  }

  private clearFilterBanner() {
    const anchor = this.filterBannerAnchor;
    if (anchor) {
      clear(anchor);
    }
  }
}
